// Shared prefs for the on-keyboard local players (slot 0 = player 1, slot
// 1 = player 2, ...). This is the single source of truth for their name, color
// and key bindings, so the Local setup and the Play/lobby setup always agree on
// player 1 & 2 - edit in one place, it shows in the other.
//
// Only name/color/keys live here (the things that should follow the human).
// Whether a slot is human or bot, maps and score stay with each screen's own
// state.
//
// Slots 0 and 1 are the original pair: the Local setup, the custom lobby and
// the account sync only know those two. Slots 2..5 are the extra players of the
// "Who's playing?" step (pages/lobby/start/WhoPlays) on the way into Play Now
// or a game room: up to MAX_LOBBY_PLAYERS share one keyboard there. Ranked and
// the Royale always seat slot 0 alone.

import { keyPairs } from "./key-util.js";
import { wormColorIds, wormColors } from "core/src/core/constants.js";
import "./worm-colors"; // the full palette, so a saved extra colour is known below
import { defaultFlagCode } from "./flags";
import { gateCosmetic, subscribeCosmeticsGate } from "./cosmetics-gate";

export interface LocalPlayerPrefs {
  name: string;
  colorId: string;
  left: string | null;
  right: string | null;
  flag: string; // the country flag code you're flying under (see shared/flags)
  // Equipped victory-animation cosmetic (a shop purchase; see shared/shop).
  // Absent/null = none equipped. The shop page is the only writer.
  victoryAnim?: string | null;
  // Equipped worm-head cosmetic (same model as victoryAnim).
  headStyle?: string | null;
}

const STORAGE_KEY = "ar.localPlayers.v1";

// How many players can share this PC from the start page.
export const MAX_LOBBY_PLAYERS = 6;

// Default name is supplied by the caller (the lobby/local screens own the fun
// random-handle generator; account sign-in supplies the account name), so this
// module stays free of any page/palette dependency beyond the core defaults.
export function defaultLocalPlayer(slot: number, name: string): LocalPlayerPrefs {
  const kp = (keyPairs as Array<{ left: string; right: string }>)[slot] || { left: null, right: null };
  const ids = wormColorIds as string[];
  return { name, colorId: ids[slot] || ids[0], left: kp.left, right: kp.right, flag: defaultFlagCode() };
}

function emptySlots(): Array<LocalPlayerPrefs | null> {
  return Array.from({ length: MAX_LOBBY_PLAYERS }, () => null);
}

function readSlots(): Array<LocalPlayerPrefs | null> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptySlots();
    const data = JSON.parse(raw);
    if (!Array.isArray(data)) return emptySlots();
    return emptySlots().map((_, i) => data[i] ?? null);
  } catch {
    return emptySlots();
  }
}

function writeSlots(slots: Array<LocalPlayerPrefs | null>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(slots));
  } catch {
    // ignore (private mode / quota)
  }
}

export function loadLocalPlayer(slot: number): LocalPlayerPrefs | null {
  const p = readSlots()[slot] ?? null;
  // A saved colour the palette doesn't know (an old or empty id) is swapped for
  // the slot's default, or it shows as a blank swatch and an uncoloured worm.
  if (p && !(wormColors as Record<string, string>)[p.colorId]) {
    const ids = wormColorIds as string[];
    return { ...p, colorId: ids[slot] || ids[0] };
  }
  return p;
}

// How many players the "Who's playing?" roster holds (1..MAX_LOBBY_PLAYERS),
// kept so the same friends are there next time. It does NOT seat anyone by
// itself: a game seats slot 0 alone unless its page asked "Who's playing?".

// How many played last time "Who's playing?" was answered: offered again next
// time, so playing alone stays one press of the button.
const WHO_KEY = "ar.whoPlays.count";
export function loadWhoCount(): number {
  try {
    const n = Math.floor(Number(localStorage.getItem(WHO_KEY)));
    return n >= 1 ? Math.min(MAX_LOBBY_PLAYERS, n) : 1;
  } catch { return 1; }
}
export function saveWhoCount(n: number): void {
  try { localStorage.setItem(WHO_KEY, String(Math.max(1, Math.min(MAX_LOBBY_PLAYERS, Math.floor(n) || 1)))); } catch { /* ignore */ }
}
// The first `count` players of this device as a game seats them.
export function localEntries(count: number): LocalPlayerPrefs[] {
  const n = Math.max(1, Math.min(MAX_LOBBY_PLAYERS, Math.floor(count) || 1));
  return Array.from({ length: n }, (_, i) => getLocalPlayer(i, i === 0 ? "Guest" : "Player " + (i + 1)));
}

const LOBBY_COUNT_KEY = "ar.localPlayers.lobbyCount";
export function loadLobbyPlayerCount(): number {
  try {
    const n = Math.floor(Number(localStorage.getItem(LOBBY_COUNT_KEY)));
    return n >= 1 ? Math.min(MAX_LOBBY_PLAYERS, n) : 1;
  } catch {
    return 1;
  }
}
export function saveLobbyPlayerCount(n: number): void {
  try {
    localStorage.setItem(LOBBY_COUNT_KEY, String(Math.max(1, Math.min(MAX_LOBBY_PLAYERS, Math.floor(n) || 1))));
  } catch {
    /* ignore */
  }
}

// The stored prefs, or a fresh default if this slot has never been set. Older saved
// data may predate `flag`, so backfill it with the locale default. Paid cosmetics
// pass through the signed-in gate: signed out they read as unequipped (the stored
// values stay put for the next sign-in).
export function getLocalPlayer(slot: number, defaultName: string): LocalPlayerPrefs {
  const loaded = loadLocalPlayer(slot);
  if (loaded) {
    return {
      ...loaded,
      flag: gateCosmetic("flag", loaded.flag) || defaultFlagCode(),
      victoryAnim: gateCosmetic("victory", loaded.victoryAnim),
      headStyle: gateCosmetic("head", loaded.headStyle),
    };
  }
  return defaultLocalPlayer(slot, defaultName);
}

// Subscribers (the mounted Local/Play screens) are notified after every write so
// they can re-read - this is what lets an account-settings download (applied via
// applyLocalPlayers) show up live on a screen that's already open.
type Listener = () => void;
const listeners = new Set<Listener>();
export function subscribeLocalPlayers(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notify(): void {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* a misbehaving listener must not break the rest */
    }
  });
}
// The gate flipping (sign-in/out resolving) changes what reads return, so
// mounted screens re-read exactly as if the prefs themselves had changed.
subscribeCosmeticsGate(notify);

// Callers that don't manage flags (the other setup screens) may omit `flag`; we then
// preserve whatever was already stored so editing a name there never wipes the flag.
// Same for the equipped victory animation (only the shop page writes it).
export function saveLocalPlayer(slot: number, prefs: Omit<LocalPlayerPrefs, "flag"> & { flag?: string }): void {
  const slots = readSlots();
  const existing = slots[slot];
  slots[slot] = {
    name: prefs.name, colorId: prefs.colorId, left: prefs.left, right: prefs.right,
    flag: prefs.flag ?? existing?.flag ?? defaultFlagCode(),
    victoryAnim: prefs.victoryAnim !== undefined ? prefs.victoryAnim : existing?.victoryAnim ?? null,
    headStyle: prefs.headStyle !== undefined ? prefs.headStyle : existing?.headStyle ?? null,
  };
  writeSlots(slots);
  notify();
}

// The shop's equip switch for slot 0 ("you"): null unequips.
export function setEquippedVictoryAnim(victoryAnim: string | null): void {
  const slots = readSlots();
  const base = slots[0] ?? defaultLocalPlayer(0, "You");
  slots[0] = { ...base, victoryAnim };
  writeSlots(slots);
  notify();
}
export function equippedVictoryAnim(): string | null {
  return gateCosmetic("victory", readSlots()[0]?.victoryAnim ?? null);
}
export function setEquippedHeadStyle(headStyle: string | null): void {
  const slots = readSlots();
  const base = slots[0] ?? defaultLocalPlayer(0, "You");
  slots[0] = { ...base, headStyle };
  writeSlots(slots);
  notify();
}
export function equippedHeadStyle(): string | null {
  return gateCosmetic("head", readSlots()[0]?.headStyle ?? null);
}

// Bulk apply (used when downloading account settings): replace slots 0 and 1 at
// once and notify, so open screens pick up the change. The extra players on
// this device (slots 2+) are not part of the account and stay as they are.
export function applyLocalPlayers(slots: Array<LocalPlayerPrefs | null>): void {
  const next = readSlots();
  next[0] = slots[0] ?? null;
  next[1] = slots[1] ?? null;
  writeSlots(next);
  notify();
}

// Take one of the extra players out (never slot 0): the players after it move
// up a slot, so the ones left stay 0..count-1 with their own prefs.
export function removeLobbyPlayer(slot: number): void {
  const count = loadLobbyPlayerCount();
  if (slot < 1 || slot >= count) return;
  const slots = readSlots();
  const removed = slots[slot];
  for (let i = slot; i < MAX_LOBBY_PLAYERS - 1; i++) slots[i] = slots[i + 1];
  // keep the removed player's prefs just past the end, so adding a player
  // again brings the same one back
  slots[count - 1] = removed;
  writeSlots(slots);
  saveLobbyPlayerCount(count - 1);
  notify();
}
